/**
 * ConnectAIModelModal.jsx
 * 
 * Inline modal for connecting organization-level LLM API key.
 * Shows when user tries to activate an agent without a configured LLM key.
 * After saving, automatically resumes the original activation action.
 * 
 * Uses EXISTING org LLM config API - does NOT touch platform key.
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { BACKEND_URL } from '../config';
import { useOrganization } from '../context/OrganizationContext';
import { fetchWithOrg } from '../utils/api';

import {
  Brain,
  CheckCircle,
  CircleNotch,
  Key,
  WarningCircle,
  X
} from '@phosphor-icons/react';
const LLM_PROVIDERS = [
  { id: 'OpenAI', name: 'OpenAI', models: ['gpt-4o', 'gpt-4o-mini', 'gpt-4-turbo', 'gpt-3.5-turbo'] },
  { id: 'Anthropic', name: 'Anthropic (Claude)', models: ['claude-3-5-sonnet-20241022', 'claude-3-haiku-20240307', 'claude-3-opus-20240229'] },
  { id: 'Google', name: 'Google (Gemini)', models: ['gemini-1.5-pro', 'gemini-1.5-flash', 'gemini-pro'] },
];

const ConnectAIModelModal = ({ isOpen, onClose, onSuccess }) => {
  const { activeOrganization } = useOrganization();
  const [provider, setProvider] = useState('OpenAI');
  const [apiKey, setApiKey] = useState('');
  const [model, setModel] = useState('gpt-4o');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const selectedProvider = LLM_PROVIDERS.find(p => p.id === provider);
  const availableModels = selectedProvider?.models || [];

  // Update default model when provider changes
  const handleProviderChange = (newProvider) => {
    setProvider(newProvider);
    const newProviderData = LLM_PROVIDERS.find(p => p.id === newProvider);
    if (newProviderData?.models?.length > 0) {
      setModel(newProviderData.models[0]);
    }
  };

  const handleSave = async () => {
    if (!apiKey.trim()) {
      setError('API key is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/org/llm-config`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            llm_provider: provider,
            llm_api_key: apiKey,
            default_model: model
          })
        },
        activeOrganization?.organization_id
      );

      if (response.ok) {
        toast.success('AI Model connected successfully');
        onSuccess?.();
        onClose();
      } else {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to save configuration');
      }
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative w-full max-w-lg mx-4 bg-[#1A1A2E] rounded-2xl border border-[rgba(255,255,255,0.1)] shadow-xl overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-[rgba(255,255,255,0.1)]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#6366F1]/20 flex items-center justify-center">
                <Brain className="text-[#6366F1]" size={20} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Connect your AI model</h2>
                <p className="text-sm text-gray-400">Required to run this agent</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-[rgba(255,255,255,0.1)] rounded-lg transition-colors"
            >
              <X size={20} className="text-gray-400" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-5 space-y-5">
            {/* Info Banner */}
            <div className="p-4 bg-[#6366F1]/10 border border-[#6366F1]/20 rounded-xl">
              <p className="text-sm text-gray-300">
                This agent requires your own LLM API key to operate. This keeps usage transparent and under your control.
              </p>
            </div>

            {/* Provider Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Provider
              </label>
              <select
                value={provider}
                onChange={(e) => handleProviderChange(e.target.value)}
                className="w-full px-4 py-3 bg-[rgba(20,20,30,0.8)] border border-[rgba(255,255,255,0.1)] rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
                data-testid="llm-provider-select"
              >
                {LLM_PROVIDERS.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>

            {/* API Key Input */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                API Key
              </label>
              <div className="relative">
                <Key size={16} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500" />
                <input
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder={`Enter your ${provider} API key`}
                  className="w-full pl-11 pr-4 py-3 bg-[rgba(20,20,30,0.8)] border border-[rgba(255,255,255,0.1)] rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
                  data-testid="llm-api-key-input"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Your API key will be encrypted and stored securely.
              </p>
            </div>

            {/* Model Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Default Model
              </label>
              <select
                value={model}
                onChange={(e) => setModel(e.target.value)}
                className="w-full px-4 py-3 bg-[rgba(20,20,30,0.8)] border border-[rgba(255,255,255,0.1)] rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
                data-testid="llm-model-select"
              >
                {availableModels.map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>

            {/* Error Message */}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <WarningCircle size={16} className="text-red-400" />
                <span className="text-sm text-red-400">{error}</span>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[rgba(255,255,255,0.1)] bg-[rgba(0,0,0,0.2)]">
            <button
              onClick={onClose}
              className="px-5 py-2.5 text-gray-300 hover:text-white hover:bg-[rgba(255,255,255,0.1)] rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !apiKey.trim()}
              className="flex items-center gap-2 px-5 py-2.5 bg-[#6366F1] hover:bg-[#5558E3] text-white font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              data-testid="connect-continue-btn"
            >
              {saving ? (
                <>
                  <CircleNotch size={16} className="animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  <CheckCircle size={16} />
                  Connect & Continue
                </>
              )}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ConnectAIModelModal;
